import TwoButton from '@/components/TwoButton';
import { style } from '@/styles/globals';
import { pr } from '@/utils/pxToRem';
import AirlineSeatReclineExtraOutlinedIcon from '@mui/icons-material/AirlineSeatReclineExtraOutlined';
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import {
  Checkbox,
  Stack,
  SwipeableDrawer,
  SwipeableDrawerProps,
  Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import styled from '@mui/styled-engine';
import Box from '@mui/system/Box';
import { useState } from 'react';
import { mappingTextFromSeatClass } from '../../../utils/seatClassMapping';
// import { LeadingIcon } from '../../../../public/icons';
import type { SeatClassType } from './type';

const StyledBox = styled(Box)({
  background: style.color.black7,
  borderRadius: pr`6px`,
  display: 'flex',
  alignItems: 'flex-end',
  marginTop: pr`8px`,
  transitionDelay: '0.1s',
  '&:hover': {
    background: '#EEF2F4',
  },
});

export const SelectStyledBox = styled(StyledBox)({
  // paddingTop: '10px',
  alignItems: 'center',
  boxSizing: 'border-box',
  height: 46,
  '.css-1r9yfjo-MuiSelect-select-MuiInputBase-input-MuiInput-input': {
    paddingRight: '8px !important',
  },
});

const StyledMenuBox = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
});
const StyledSelect = styled(Select)`
  div.MuiSelect-select.Mui-disabled {
    -webkit-text-fill-color: ${style.color.black2} !important;
  }
`;

const MenuItemLayout = ({
  label,
  caption,
  checked,
  onCheck,
}: {
  label: string;
  caption: string;
  checked: boolean;
  onCheck: () => void;
}) => {
  const handleAdjustSeatClass = () => {
    onCheck();
  };
  return (
    <Stack
      onClick={(e) => {
        e.stopPropagation();
        handleAdjustSeatClass();
      }}
      alignItems="center"
      direction="row"
      spacing={pr`16px`}
    >
      <Stack
        style={{
          justifyContent: 'start',
          flexBasis: '90%',
        }}
      >
        <Typography
          variant="body2"
          fontWeight="bold"
        >
          {label}
        </Typography>
        <Typography variant="caption">{caption}</Typography>
      </Stack>
      <StyledMenuBox
        style={{
          justifyContent: 'end',
          flexBasis: '10%',
        }}
      >
        <Checkbox
          style={{ color: style.color.blue3 }}
          icon={<RadioButtonUncheckedOutlinedIcon />}
          checkedIcon={<RadioButtonCheckedOutlinedIcon />}
          checked={checked}
        />
      </StyledMenuBox>
    </Stack>
  );
};

const BottomDrawer = ({ children, ...props }: SwipeableDrawerProps) => (
  <SwipeableDrawer
    {...props}
    anchor="bottom"
    PaperProps={{
      ...props.PaperProps,
      style: {
        ...props.PaperProps?.style,
        width: '100%',
        margin: 0,
        position: 'absolute',
        left: 0,
        bottom: 0,
        background: 'FFFFFF',
        boxShadow:
          '0px 0px 0px 1px rgba(28, 59, 84, 0.05), 0px -2px 6px -2px rgba(28, 59, 84, 0.04), 0px -8px 12px -3px rgba(28, 59, 84, 0.08)',
        borderRadius: '16px 16px 0px 0px',
      },
    }}
  >
    {children}
  </SwipeableDrawer>
);

const ChooseSeatClass = ({
  seatClass,
  adjustSeatClass,
}: {
  seatClass: SeatClassType;
  adjustSeatClass: (param: SeatClassType) => void;
}) => {
  const [openSeatClassDialog, setOpenSeatClassDialog] = useState(false);
  /* eslint-disable max-len */

  const [checkedOption, setCheckedOption] = useState<SeatClassType>(seatClass);
  return (
    <>
      <SelectStyledBox
        sx={{
          flex: 1,
          ml: pr`4px`,
          overflow: 'auto',
          cursor: 'pointer',
        }}
        onClick={() => {
          setOpenSeatClassDialog(true);
        }}
      >
        <div
          style={{
            margin: '0 0.5em 0em 0.8em',
            display: 'flex',
            color: '#4F4F4F',
          }}
        >
          <AirlineSeatReclineExtraOutlinedIcon />
        </div>
        <FormControl
          sx={{ flex: 1 }}
          variant="standard"
        >
          {/* <InputLabel
            style={{
              top: pr`8px`,
              color: style.color.black1,
            }}
            id="seatClass"
          >
            Hạng ghế
          </InputLabel> */}
          <StyledSelect
            value={10}
            fullWidth
            disableUnderline
            disabled
            IconComponent={() => <div />}
            labelId="seatClass"
            SelectDisplayProps={{
              style: {
                padding: 0,
                //! Notice
                fontWeight: 'bold',
                fontSize: '1rem',
              },
            }}
          >
            <MenuItem value={10}>
              {mappingTextFromSeatClass(seatClass)}
            </MenuItem>
          </StyledSelect>
        </FormControl>
      </SelectStyledBox>
      <BottomDrawer
        onOpen={() => { }}
        onClose={() => {
          setOpenSeatClassDialog(false);
          setCheckedOption(seatClass);
        }}
        open={openSeatClassDialog}
      >
        <Stack
          spacing="1.75em"
          sx={{ p: '1.5em 1em' }}
        >
          <Stack
            spacing={pr`16px`}
          >
            <Stack>
              <Typography variant="h6">Loại vé</Typography>
            </Stack>
            <Stack spacing="1.2em">
              <MenuItemLayout
                label="Phổ thông"
                caption="Bay tiết kiệm, đáp ứng mọi nhu cầu cơ bản của bạn"
                checked={checkedOption === 'PT'}
                onCheck={() => setCheckedOption('PT')}
              />
              <MenuItemLayout
                label="Phổ thông đặc biệt"
                caption="Chi phí hợp lý với bữa ăn ngon và chỗ để chân rộng rãi"
                checked={checkedOption === 'PTDB'}
                onCheck={() => setCheckedOption('PTDB')}
              />
              <MenuItemLayout
                label="Thương gia"
                caption="Bay đẳng cấp, với quầy làm thủ tục và khu ghế ngồi riêng"
                checked={checkedOption === 'TG'}
                onCheck={() => setCheckedOption('TG')}
              />
              {/* enable if international route, support later
          <MenuItemLayout
            label="Hạng nhất"
            type="First Class"
            caption="Hạng cao cấp nhất, với dịch vụ 5 sao được cá nhân hóa"
          /> */}
            </Stack>
          </Stack>
          <TwoButton
            leftTitle="Huỷ"
            leftVariant="contained"
            rightVariant="contained"
            rightTitle="Áp dụng"
            onLeftClick={() => {
              setOpenSeatClassDialog(false);
              setCheckedOption(seatClass);
            }}
            onRightClick={() => {
              setOpenSeatClassDialog(false);
              adjustSeatClass(checkedOption);
            }}
          />

        </Stack>
      </BottomDrawer>
    </>
  );
};

export default ChooseSeatClass;
